import { inject, Injectable } from "@angular/core";
import { DbSchemeEnum } from "../enums/db-scheme.enum";
import { ProcedureNameEnum } from "../enums/procedure-name.enum";
import { LogService } from "./log.service";
import { LoginService } from "./login.service";
import { SobitErrorCode } from "./model/sobit.model";
import {
  ICreateEftLogInput,
  ICreateEftLogResult,
  IEftLogEntry,
} from "./model/log.model";
import { IExecuteProcedureRequest } from "./model/pos.api.model";
import { SharedApiService } from "./api/sharedApi.service";

type LogSeverity = "Info" | "Warning" | "Error" | "Critical";

interface IEftLogDetails extends Partial<Omit<ICreateEftLogInput, "action" | "created">> {
  note?: string | null;
  message?: string | null;
  text?: string | null;
  methodName?: string | null;
  details?: Record<string, unknown>;
}

interface IInternalEftLogInput extends ICreateEftLogInput {
  severity: LogSeverity;
}

@Injectable({
  providedIn: "root",
})
export class EftLogService {
  private readonly sharedApiService = inject(SharedApiService);
  private readonly logService = inject(LogService);
  private readonly loginService = inject(LoginService);
  private readonly storageKey = "pos.eftlog.queue";
  private readonly queueLimit = 100;
  private readonly fallbackQueue: IEftLogEntry[] = [];
  private isFlushing = false;

  constructor() {
    if (this.canUseBrowserApis()) {
      window.addEventListener("online", () => {
        void this.flushQueue();
      });
    }

    this.loginService.loginChanged.subscribe((user) => {
      if (user && this.loginService.loggedIn) {
        void this.flushQueue();
      }
    });
  }

  public async createLogEntry(input: IInternalEftLogInput): Promise<void> {
    
    const entry = this.buildLogEntry(input);
    this.enqueue(entry);
    await this.flushQueue();
    
  }

  public async logDebug(action: SobitErrorCode, details?: IEftLogDetails): Promise<void> {
    await this.createLogEntry({ action, severity: "Info", ...details });
  }

  public async logInfo(action: SobitErrorCode, details?: IEftLogDetails): Promise<void> {
    await this.createLogEntry({ action, severity: "Info", ...details });
  }

  public async logWarning(action: SobitErrorCode, details?: IEftLogDetails): Promise<void> {
    await this.createLogEntry({ action, severity: "Warning", ...details });
  }

  public async logError(action: SobitErrorCode, details?: IEftLogDetails): Promise<void> {
    await this.createLogEntry({ action, severity: "Error", ...details });
  }

  public async logCritical(action: SobitErrorCode, details?: IEftLogDetails): Promise<void> {
    await this.createLogEntry({ action, severity: "Critical", ...details });
  }

  public getQueuedCount(): number {
    return this.readQueue().length;
  }

  public clearQueue(): void {
    this.writeQueue([]);
  }

  public async flushQueue(): Promise<void> {
    if (this.isFlushing || !this.canUseBrowserApis() || !this.isOnline()) {
      return;
    }

    this.isFlushing = true;

    try {
      while (true) {
        const queue = this.readQueue();
        const nextEntry = queue[0];

        if (!nextEntry) {
          return;
        }

        try {
          const sent = await this.sendLogEntry(nextEntry);

          if (!sent) {
            return;
          }

          queue.shift();
          this.writeQueue(queue);
        } catch (error) {
          console.error("Failed to send EFT log entry.", error);
          return;
        }
      }
    } finally {
      this.isFlushing = false;
    }
  }

  private enqueue(entry: IEftLogEntry): void {
    const queue = this.readQueue();
    queue.push(entry);

    while (queue.length > this.queueLimit) {
      queue.shift();
    }

    this.writeQueue(queue);
  }

  private async sendLogEntry(entry: IEftLogEntry): Promise<boolean> {
    if (!this.loginService.loggedIn) {
      return false;
    }

    const request: IExecuteProcedureRequest<IEftLogEntry> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.CreateEftLog,
      Parameters: entry,
    };

    await this.sharedApiService.executeProcedure<IEftLogEntry, ICreateEftLogResult>(request);
    return true;
  }

  private buildLogEntry(input: IInternalEftLogInput): IEftLogEntry {
    const created = this.normalizeDateTime(input.created ?? input.eventTime ?? new Date());
    const eventTime = this.normalizeDateTime(input.eventTime ?? input.created ?? new Date());
    const note = this.normalizeNullableString(
      input.note ?? this.buildNote(input.action, input.severity, input.methodName, input as unknown as Partial<ICreateEftLogInput> & Record<string, unknown>),
    );

    return {
      Action: input.action,
      Created: created,
      DeviceID: this.normalizeNumber(input.deviceId),
      DocumentNumber: this.buildDocumentNumber(input),
      EventTime: eventTime,
      JournalNumber: this.normalizeNullableString(input.journalNumber ?? input.paymentUID),
      Message: this.normalizeNullableString(input.message),
      Note: note,
      ProcessId: this.normalizeNumber(input.processId),
      TerminalID: this.normalizeNullableString(input.terminalId),
      Text: this.normalizeNullableString(input.text),
      ThreadId: this.normalizeNumber(input.threadId),
    };
  }

  private buildNote(
    action: number,
    severity: LogSeverity,
    methodName: string | null | undefined,
    details?: Partial<ICreateEftLogInput> & Record<string, unknown>,
  ): string {
    const {
      message: _message,
      text: _text,
      note: _note,
      action: _action,
      created: _created,
      paymentUID: _paymentUID,
      deviceId: _deviceId,
      documentNumber: _documentNumber,
      eventTime: _eventTime,
      journalNumber: _journalNumber,
      browserFingerprint: _browserFingerprint,
      processId: _processId,
      terminalId: _terminalId,
      threadId: _threadId,
      ...rest
    } = details ?? {};

    return JSON.stringify(this.removeNullish({
      code: this.getActionCode(action),
      severity,
      methodName: methodName ?? null,
      browserFingerprint: this.normalizeNullableString(
        details?.browserFingerprint ?? this.logService.getBrowserFingerprint(),
      ),
      paymentUID: this.normalizeNullableString(details?.paymentUID),
      ...rest,
    }));
  }

  private buildDocumentNumber(input: IInternalEftLogInput): string | null {
    const documentNumberCandidates = [
      input.bnr,
      input.variableNumber,
      input.voucherNumber,
      input.documentNumber,
    ];

    for (const candidate of documentNumberCandidates) {
      const normalizedCandidate = this.normalizeNullableString(candidate);

      if (normalizedCandidate) {
        return normalizedCandidate;
      }
    }

    return null;
  }

  private getActionCode(action: number): string {
    return (SobitErrorCode as unknown as Record<number, string>)[action] ?? String(action);
  }

  private removeNullish<T extends Record<string, unknown>>(value: T): T {
    return Object.entries(value).reduce((acc, [key, current]) => {
      if (current !== undefined && current !== null && current !== "") {
        acc[key as keyof T] = current as T[keyof T];
      }

      return acc;
    }, {} as T);
  }

  private normalizeDateTime(value: string | Date): string {
    if (value instanceof Date) {
      return value.toISOString();
    }

    return value;
  }

  private normalizeNumber(value?: number | null): number {
    return Number.isFinite(value) ? Math.trunc(value ?? 0) : 0;
  }

  private normalizeNullableString(value: unknown): string | null {
    if (typeof value !== "string") {
      return value === null || value === undefined ? null : String(value);
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date) && !(value instanceof Error);
  }

  private readQueue(): IEftLogEntry[] {
    if (!this.canUseBrowserApis()) {
      return [...this.fallbackQueue];
    }

    try {
      const serializedQueue = localStorage.getItem(this.storageKey);

      if (!serializedQueue) {
        return [];
      }

      const parsedQueue = JSON.parse(serializedQueue);
      return Array.isArray(parsedQueue) ? parsedQueue : [];
    } catch {
      return [...this.fallbackQueue];
    }
  }

  private writeQueue(queue: IEftLogEntry[]): void {
    const limitedQueue = queue.slice(-this.queueLimit);

    if (!this.canUseBrowserApis()) {
      this.fallbackQueue.splice(0, this.fallbackQueue.length, ...limitedQueue);
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(limitedQueue));
    } catch {
      this.fallbackQueue.splice(0, this.fallbackQueue.length, ...limitedQueue);
    }
  }

  private canUseBrowserApis(): boolean {
    return typeof window !== "undefined" && typeof localStorage !== "undefined";
  }

  private isOnline(): boolean {
    return typeof navigator !== "undefined" ? navigator.onLine : true;
  }
}
